import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { csv, scaleLinear, line } from 'd3'
import { ChartAxisText } from 'components/styles'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'

export const ChartEvolution = () => {
  const t = useTranslation().t
  const theme = useTheme()
  const isMobile = window.innerWidth < 768
  const [data, setData] = useState<any>([])
  const svg = useRef<SVGSVGElement | null>(null)
  const svgContainer = useRef<HTMLDivElement | null>(null)
  const ratios = {
    desktop: 4 / 6,
    mobile: 1 / 1,
  }

  const [width, setWidth] = useState(0)
  const margin = {
    top: 5,
    right: 30,
    bottom: 40,
    left: isMobile ? 60 : 100,
  }
  const height =
    width * (isMobile ? ratios.mobile : ratios.desktop) -
    margin.top -
    margin.bottom

  // Y axis
  const scaleY = scaleLinear()
    .domain([
      30,
      Math.max.apply(
        null,
        data.map((item: any) => +item.value_imported)
      ),
    ])
    .range([height, 0])

  // X axis
  const scaleX = scaleLinear()
    .domain([
      Math.min.apply(
        null,
        data.map((item: any) => +item.year)
      ),
      Math.max.apply(
        null,
        data.map((item: any) => +item.year)
      ),
    ])
    .range([0, width])

  const handleResize = () => {
    if (svgContainer.current) {
      setWidth(
        svgContainer.current?.getBoundingClientRect().width -
          margin.left -
          margin.right
      )
    }
  }

  useLayoutEffect(() => {
    handleResize()
  }, [svgContainer])

  useEffect(() => {
    csv(
      `${
        process.env.NODE_ENV === 'production'
          ? process.env.REACT_APP_DEPLOY_PATH
          : ''
      }/data/evolution.csv`
    ).then((d: d3.DSVRowArray) => {
      setData(d)
    })

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const ticksX = scaleX.ticks(isMobile ? 4 : 7)
  const ticksY = scaleY.ticks(5)

  const linePathCountry = line()(
    data
      .slice(1, data.length)
      .map((d: any) => [scaleX(+d.year), scaleY(+d.value)])
  )
  const linePathImported = line()(
    data
      .slice(1, data.length)
      .map((d: any) => [scaleX(+d.year), scaleY(+d.value_imported)])
  )

  return (
    <div ref={svgContainer}>
      <svg
        ref={svg}
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g
          style={{ transform: `translate(${margin.left}px, ${margin.top}px)` }}
        >
          {/* Grid X */}
          {ticksX.map((tick: any) => (
            <g style={{ transform: `translateX(${scaleX(tick)}px)` }}>
              <line
                key={`grid-line-${tick}`}
                x1={0}
                y1={0}
                x2={0}
                y2={height}
                stroke='#33333333'
                strokeDasharray='1px 1px'
              />
              <ChartAxisText
                x={0}
                y={height + margin.bottom - 10}
                textAnchor={isMobile ? 'end' : 'middle'}
              >
                {tick}
              </ChartAxisText>
            </g>
          ))}

          {/* Grid Y */}
          {ticksY.map((tick: any, i: number) => (
            <g style={{ transform: `translateY(${scaleY(tick)}px)` }}>
              <line
                key={`grid-line-${tick}`}
                x1={0}
                y1={0}
                x2={width}
                y2={0}
                stroke='#33333333'
                strokeDasharray='1px 1px'
              />
              <ChartAxisText x={-8} y={5} textAnchor='end'>
                {tick}
                {/* {i === ticksY.length - 1 ? (
                  <>
                    MtCo<tspan>2</tspan>
                  </>
                ) : (
                  ''
                )} */}
              </ChartAxisText>
            </g>
          ))}

          <ChartAxisLabel x={-10} y={10} textAnchor='end'>
            MtCO
            <tspan baseline-shift='sub' font-size='smaller'>
              2
            </tspan>
          </ChartAxisLabel>

          <path
            d={linePathCountry as string | undefined}
            stroke='var(--text-color)'
            strokeWidth={isMobile ? 3 : 4}
            fill='none'
          />

          <path
            d={linePathImported as string | undefined}
            stroke={theme.brandColor}
            strokeWidth={isMobile ? 3 : 4}
            fill='none'
          />

          <ChartLineLabel
            x={scaleX(2000)}
            y={scaleY(48)}
            fill='var(--text-color)'
            textAnchor='start'
            paintOrder='stroke'
            stroke='var(--site-background)'
            strokeWidth={8}
            strokeLinecap='butt'
            strokeLinejoin='round'
          >
            <tspan x={scaleX(2000)}>
              {t('evolutionChart:notes.swiss.line1')}
            </tspan>
            <tspan dy='1.2em' x={scaleX(2000)}>
              {t('evolutionChart:notes.swiss.line2')}
            </tspan>
          </ChartLineLabel>

          <ChartLineLabel
            x={scaleX(1995)}
            y={scaleY(70)}
            fill={theme.brandColor}
            textAnchor='start'
            paintOrder='stroke'
            stroke='var(--site-background)'
            strokeWidth={8}
            strokeLinecap='butt'
            strokeLinejoin='round'
          >
            <tspan x={scaleX(isMobile ? 2007 : 1995)}>
              {t('evolutionChart:notes.imported.line1')}
            </tspan>
            <tspan dy='1.2em' x={scaleX(isMobile ? 2007 : 1995)}>
              {t('evolutionChart:notes.imported.line2')}
            </tspan>
          </ChartLineLabel>

          {/* Lines */}
          {/* {data.map((d: any) => {
            const y = scaleY(d.country) || 0

            return (
              <rect
                key={`bar-${d.country}`}
                onClick={() => {
                  console.log(d.country)
                }}
                width={scaleX(+d.value)}
                height={2}
                x={0}
                y={y}
                fill={d.country === 'Switzerland' ? '#A757A3' : '#A757A3'}
              />
            )
          })} */}
        </g>
      </svg>
    </div>
  )
}

const ChartLineLabel = styled.text`
  font-size: 0.85em;
  font-weight: 700;
`

const ChartAxisLabel = styled.text`
  font-size: 0.7em;
  fill: var(--text-color);
  font-weight: 900;
  -webkit-font-smoothing: antialiased;
`
